import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from '@reduxjs/toolkit';

import { IUser } from '../../UserSlice';
import { RootState } from '../..';
import {
  adminCreateCoursePricingTemplate,
  adminDeleteCoursePricingTemplate,
  adminGetCoursePricingTemplates,
  adminGetCoursePricingTemplatesForDropdown,
  adminUpdateCoursePricingTemplate,
  adminVerifyCoursePricingTemplateDelete,
} from './adminApi';

export const COURSE_PRICING_TEMPLATE_SUBSCRIPTION_PRICING_BILLING_PERIOD_TYPES = {
  DAYS: 'DAYS',
  WEEKS: 'WEEKS',
  MONTHS: 'MONTHS',
  YEARS: 'YEARS',
} as const;

export type TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes = typeof COURSE_PRICING_TEMPLATE_SUBSCRIPTION_PRICING_BILLING_PERIOD_TYPES[keyof typeof COURSE_PRICING_TEMPLATE_SUBSCRIPTION_PRICING_BILLING_PERIOD_TYPES];

export interface ICoursePricingTemplateOneTimePricing {
  currency: string;
  amount: number;
  country?: string;
}

export interface ICoursePricingTemplateSubscriptionPricing {
  currency: string;
  amount: number;
  billingPeriod: {
    count: number;
    type: TCoursePricingTemplateSubscriptionPricingBillingPeriodTypes;
  };
  country?: string;
}

export interface ICoursePricingTemplate {
  _id: string;
  name: string;
  credit: boolean;
  oneTime: boolean;
  subscription: boolean;
  createdBy: string | IUser;
  updatedBy: string | IUser;
  oneTimePricings?: ICoursePricingTemplateOneTimePricing[];
  subscriptionPricings?: ICoursePricingTemplateSubscriptionPricing[];
}

export interface ICoursePricingTemplateDeleteVerificationData {
  coursesCount: number;
}

export interface IInitialCoursePricingTemplatesState {
  status: 'idle' | 'loading' | 'rejected';
  // Admin
  adminCoursePricingTemplateListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICoursePricingTemplate>;
  };
  adminCoursePricingTemplateListDataForDropdown?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ICoursePricingTemplate>;
  };
  adminCoursePricingTemplateDeleteVerificationData?: ICoursePricingTemplateDeleteVerificationData;
}

const initialState: IInitialCoursePricingTemplatesState = {
  status: 'idle',
  // Admin
  adminCoursePricingTemplateListData: undefined,
  adminCoursePricingTemplateListDataForDropdown: undefined,
  adminCoursePricingTemplateDeleteVerificationData: undefined,
};

export const CoursePricingTemplatesSlice = createSlice({
  initialState,
  name: 'coursePricingTemplatesSlice',
  reducers: {
    resetAdminCoursePricingTemplateListData: (state) => {
      state.adminCoursePricingTemplateListData = undefined;
    },
    resetAdminCoursePricingTemplateListDataForDropdown: (state) => {
      state.adminCoursePricingTemplateListDataForDropdown = undefined;
    },
    resetAdminCoursePricingTemplateDeleteVerificationData: (state) => {
      state.adminCoursePricingTemplateDeleteVerificationData = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(adminGetCoursePricingTemplates.fulfilled, (state, action) => {
        const result = action.payload;

        const newAdminCoursePricingTemplateListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
        };

        state.adminCoursePricingTemplateListData = newAdminCoursePricingTemplateListData;
      })
      .addCase(
        adminGetCoursePricingTemplatesForDropdown.fulfilled,
        (state, action) => {
          const result = action.payload;

          const newAdminCoursePricingTemplateListDataForDropdown = {
            limit: result.limit,
            offset: result.offset,
            totalDocs: result.totalDocs || 0,
            list: result.docs || [],
          };

          state.adminCoursePricingTemplateListDataForDropdown = newAdminCoursePricingTemplateListDataForDropdown;
        }
      )
      .addCase(adminCreateCoursePricingTemplate.fulfilled, (state, action) => {
        const result = action.payload;

        if (state.adminCoursePricingTemplateListData) {
          const newAdminCoursePricingTemplateListData = {
            ...state.adminCoursePricingTemplateListData,
          };

          newAdminCoursePricingTemplateListData.list.unshift(result);

          if (newAdminCoursePricingTemplateListData.totalDocs) {
            newAdminCoursePricingTemplateListData.totalDocs += 1;
          } else {
            newAdminCoursePricingTemplateListData.totalDocs = 1;
          }

          state.adminCoursePricingTemplateListData = newAdminCoursePricingTemplateListData;
        }
      })
      .addCase(adminUpdateCoursePricingTemplate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminCoursePricingTemplateListData &&
          state.adminCoursePricingTemplateListData.list &&
          state.adminCoursePricingTemplateListData.list.length
        ) {
          const newAdminCoursePricingTemplateListData = {
            ...state.adminCoursePricingTemplateListData,
          };

          const index = newAdminCoursePricingTemplateListData.list.findIndex(
            (item) => item._id === result._id
          );

          if (index > -1) {
            newAdminCoursePricingTemplateListData.list[index] = result;
          }

          state.adminCoursePricingTemplateListData = newAdminCoursePricingTemplateListData;
        }
      })
      .addCase(adminDeleteCoursePricingTemplate.fulfilled, (state, action) => {
        const result = action.payload;

        if (
          state.adminCoursePricingTemplateListData &&
          state.adminCoursePricingTemplateListData.list &&
          state.adminCoursePricingTemplateListData.list.length
        ) {
          const newAdminCoursePricingTemplateListData = {
            ...state.adminCoursePricingTemplateListData,
          };

          newAdminCoursePricingTemplateListData.totalDocs = newAdminCoursePricingTemplateListData.totalDocs
            ? newAdminCoursePricingTemplateListData.totalDocs - 1
            : 0;

          newAdminCoursePricingTemplateListData.list = newAdminCoursePricingTemplateListData.list.filter(
            (item) => item._id !== result._id
          );

          state.adminCoursePricingTemplateListData = newAdminCoursePricingTemplateListData;
        }
      })
      .addCase(
        adminVerifyCoursePricingTemplateDelete.fulfilled,
        (state, action) => {
          const result = action.payload;

          state.adminCoursePricingTemplateDeleteVerificationData = result;
        }
      );

    // Common Matchers
    builder.addMatcher(
      isPending(
        // COURSE PRICING TEMPLATES
        adminCreateCoursePricingTemplate,
        adminGetCoursePricingTemplates,
        adminUpdateCoursePricingTemplate,
        adminDeleteCoursePricingTemplate,
        adminVerifyCoursePricingTemplateDelete
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isFulfilled(
        // COURSE PRICING TEMPLATES
        adminCreateCoursePricingTemplate,
        adminGetCoursePricingTemplates,
        adminUpdateCoursePricingTemplate,
        adminDeleteCoursePricingTemplate,
        adminVerifyCoursePricingTemplateDelete
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isRejected(
        // COURSE PRICING TEMPLATES
        adminCreateCoursePricingTemplate,
        adminGetCoursePricingTemplates,
        adminUpdateCoursePricingTemplate,
        adminDeleteCoursePricingTemplate,
        adminVerifyCoursePricingTemplateDelete
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getCoursePricingTemplatesState = (
  state: RootState
): RootState['coursePricingTemplates'] => state.coursePricingTemplates;

export const {
  resetAdminCoursePricingTemplateListData,
  resetAdminCoursePricingTemplateListDataForDropdown,
  resetAdminCoursePricingTemplateDeleteVerificationData,
} = CoursePricingTemplatesSlice.actions;

export * from './adminApi';
export default CoursePricingTemplatesSlice.reducer;
